import { useState, useEffect, useMemo, useCallback } from 'react';
import { ImageToCadModel, Model } from '@shared/types';
import {
  getModelDefaultPolygonCount,
  getMaxPolygonCount,
} from '@/constants/meshConstants';
import {
  readStoredPolygonOverrides,
  writeStoredPolygonOverrides,
  writeStoredMeshTopology,
} from './persistence';

interface UsePolygonControlsArgs {
  type: 'parametric' | 'imagetocad';
  model: Model;
  meshTopology: 'quads' | 'polys';
  setMeshTopology: (topology: 'quads' | 'polys') => void;
}

interface UsePolygonControlsResult {
  setPolygonCountForCurrentModel: (count: number) => void;
  handleMeshTopologyChange: (newTopology: 'quads' | 'polys') => void;
  polygonCount: number;
  resetPolygonCount: () => void;
  maxPolygonCount: number;
}

// Polygon-count / mesh-topology derived state, callbacks, persistence and
// reset logic, extracted verbatim from TextAreaChat. This is a contiguous
// run of root hooks (overrides state → callbacks → memo → persist effects →
// clear-on-model-change effect); calling it in their place preserves the
// root's observable hook order. `meshTopology`/`setMeshTopology` remain owned
// by the root (its own `useState` sits earlier in the order) and are passed
// in so the topology-change + persist-topology effect keep working.
export function usePolygonControls({
  type,
  model,
  meshTopology,
  setMeshTopology,
}: UsePolygonControlsArgs): UsePolygonControlsResult {
  // Polygon count state - single source of truth for user overrides
  const [polygonOverrides, setPolygonOverrides] = useState<
    Record<string, number>
  >(() => readStoredPolygonOverrides());

  // Set polygon count for current model+topology combination
  const setPolygonCountForCurrentModel = useCallback(
    (count: number) => {
      if (type !== 'imagetocad') return;
      const modelTopologyKey = `${model}-${meshTopology}`;
      const defaultCount = getModelDefaultPolygonCount(
        model as ImageToCadModel,
        meshTopology,
      );

      // If setting to default, remove the override instead of storing it
      if (count === defaultCount) {
        setPolygonOverrides((prev) => {
          const { [modelTopologyKey]: _, ...rest } = prev;
          return rest;
        });
      } else {
        setPolygonOverrides((prev) => ({
          ...prev,
          [modelTopologyKey]: count,
        }));
      }
    },
    [model, meshTopology, type],
  );

  // Persist meshTopology changes to localStorage
  const handleMeshTopologyChange = useCallback(
    (newTopology: 'quads' | 'polys') => {
      setMeshTopology(newTopology);

      // Reset polygon count to the model-specific default for the new topology
      const modelSpecificDefault = getModelDefaultPolygonCount(
        model as ImageToCadModel,
        newTopology,
      );
      setPolygonCountForCurrentModel(modelSpecificDefault);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setMeshTopology is the root's stable useState setter (omitted from deps verbatim as in the original inline callback); only model + setPolygonCountForCurrentModel drive re-creation.
    [setPolygonCountForCurrentModel, model],
  );

  // Derived polygon count - no useState needed, calculated from model + topology + overrides
  const polygonCount = useMemo(() => {
    if (type !== 'imagetocad') return 0;
    const modelTopologyKey = `${model}-${meshTopology}`;
    const userOverride = polygonOverrides[modelTopologyKey];
    return (
      userOverride ??
      getModelDefaultPolygonCount(model as ImageToCadModel, meshTopology)
    );
  }, [model, meshTopology, polygonOverrides, type]);

  // Persist polygon overrides to localStorage
  useEffect(() => {
    writeStoredPolygonOverrides(polygonOverrides);
  }, [polygonOverrides]);

  // Persist mesh topology to localStorage
  useEffect(() => {
    writeStoredMeshTopology(meshTopology);
  }, [meshTopology]);

  // Reset polygon count to default for current model and topology
  const resetPolygonCount = useCallback(() => {
    const modelTopologyKey = `${model}-${meshTopology}`;
    setPolygonOverrides((prev) => {
      const { [modelTopologyKey]: _, ...rest } = prev;
      return rest;
    });
  }, [model, meshTopology]);

  // When model changes, clear any polygon overrides to use the new model's defaults
  useEffect(() => {
    if (type !== 'imagetocad') return;

    // Clear all overrides when switching models to ensure we use the new model's defaults
    setPolygonOverrides({});
  }, [model, type]);

  // Computed polygon values for server submission
  const maxPolygonCount =
    type === 'imagetocad'
      ? getMaxPolygonCount(model as ImageToCadModel, meshTopology)
      : 0;

  return {
    setPolygonCountForCurrentModel,
    handleMeshTopologyChange,
    polygonCount,
    resetPolygonCount,
    maxPolygonCount,
  };
}
